// Password-reset flow — Admin-approval based:
// User enters phone → request is submitted to Admin → user calls/contacts Admin
// → Admin approves and shares a 6-digit code → user enters code + new password.
// No SMS is sent anymore.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { extractError } from "@/api/client";
import { X, Phone, KeyRound, ArrowLeft, CheckCircle2, ShieldCheck, PhoneCall, AtSign } from "lucide-react";
import SupportContactsCard from "@/components/SupportContactsCard";
import PasswordInput from "@/components/PasswordInput";

export default function ForgotPasswordModal({ onClose, initialPhone = "" }) {
  const [step, setStep] = useState(1); // 1: submit identifier, 2: enter code+new pw
  const [identifier, setIdentifier] = useState(initialPhone);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const { setSessionFromReset } = useAuth();

  const isEmail = identifier.includes("@");

  const submitRequest = async () => {
    setErr(""); setBusy(true);
    try {
      await api.post("/auth/forgot-password", { identifier: identifier.trim() });
      setStep(2);
    } catch (e) { setErr(extractError(e)); }
    finally { setBusy(false); }
  };

  const reset = async () => {
    if (password !== password2) { setErr("كلمة المرور غير متطابقة"); return; }
    if (password.length < 6) { setErr("يجب أن تكون كلمة المرور 6 أحرف على الأقل"); return; }
    if (!/^\d{6}$/.test(code)) { setErr("الرمز يجب أن يكون 6 أرقام"); return; }
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", {
        identifier: identifier.trim(), code, new_password: password,
      });
      if (setSessionFromReset) setSessionFromReset(data);
      onClose?.();
      const role = data?.user?.role || "customer";
      nav(role === "admin" ? "/admin" : role === "driver" ? "/driver" : role === "restaurant" ? "/restaurant" : "/");
    } catch (e) { setErr(extractError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4" data-testid="forgot-modal">
      <div className="bg-white w-full max-w-md rounded-3xl talabya-shadow-soft overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-l from-primary-brand to-blue-500 text-white px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <KeyRound size={18}/>
            <div className="font-black text-lg" style={{fontFamily:"Cairo"}}>استعادة كلمة المرور</div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-lg bg-white/15 hover:bg-white/25 flex items-center justify-center" data-testid="forgot-close">
            <X size={16}/>
          </button>
        </div>

        <div className="p-6 max-h-[75vh] overflow-y-auto">
          {step === 1 && (
            <>
              <div className="mb-4 p-3 rounded-xl bg-blue-50 border border-blue-200 flex items-start gap-2">
                <ShieldCheck size={16} className="text-primary-brand mt-0.5 shrink-0"/>
                <div className="text-xs text-[var(--text-primary)] leading-relaxed">
                  <b>ملاحظة أمان:</b> لأمانك، إعادة تعيين كلمة المرور تتطلب موافقة الإدارة اليدوية.
                  <ol className="mt-2 mr-3 list-decimal space-y-1 text-[var(--text-secondary)]">
                    <li>أدخل رقم هاتفك أو بريدك الإلكتروني المسجّل هنا.</li>
                    <li>تواصل مع إدارة طلبية وأخبرهم أنك طلبت استعادة كلمة المرور.</li>
                    <li>الإدارة ستُعطيك <b className="text-primary-brand">كوداً من 6 أرقام</b>.</li>
                    <li>أدخل الكود مع كلمة المرور الجديدة في الخطوة التالية.</li>
                  </ol>
                </div>
              </div>
              <label className="text-sm font-bold">رقم الهاتف أو البريد الإلكتروني</label>
              <div className="relative mt-1">
                {isEmail
                  ? <AtSign size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]"/>
                  : <Phone size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]"/>}
                <input
                  type="text" dir="ltr" value={identifier} onChange={(e) => setIdentifier(e.target.value)}
                  className="w-full pr-9 pl-4 py-3 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none text-right"
                  placeholder="09XXXXXXXX أو example@mail.com"
                  autoComplete="username"
                  data-testid="forgot-identifier"
                />
              </div>
              <div className="text-[10px] text-[var(--text-secondary)] mt-1">
                يمكنك استخدام رقم الهاتف الذي سجّلت به أو بريدك الإلكتروني إن كان مضبوطاً.
              </div>
              {err && <div className="mt-3 text-sm bg-red-50 text-red-600 px-3 py-2 rounded-lg" data-testid="forgot-error">{err}</div>}
              <button disabled={busy || !identifier} onClick={submitRequest}
                className="mt-5 w-full py-3 rounded-xl bg-primary-brand text-white font-black disabled:opacity-60 flex items-center justify-center gap-2"
                data-testid="forgot-send-code">
                <PhoneCall size={15}/> {busy ? "جاري الإرسال…" : "إرسال طلب للإدارة"}
              </button>
              <button
                onClick={() => setStep(2)}
                className="mt-2 w-full py-2 rounded-xl text-primary-brand text-sm font-bold hover:bg-blue-50"
                data-testid="forgot-have-code"
              >
                لديّ كود من الإدارة — إدخال مباشر
              </button>

              <div className="mt-4">
                <SupportContactsCard
                  variant="compact"
                  title="كيف تتواصل مع الإدارة"
                  subtitle="اختر أي قناة أدناه للحصول على الكود"
                />
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <button onClick={() => setStep(1)} className="text-xs text-primary-brand mb-3 flex items-center gap-1" data-testid="forgot-back">
                <ArrowLeft size={12}/> تغيير المُعرِّف
              </button>
              <div className="mb-3 text-xs bg-green-50 text-green-700 px-3 py-2 rounded-lg flex items-start gap-2" data-testid="forgot-msg">
                <CheckCircle2 size={14} className="mt-0.5 shrink-0"/>
                <span>
                  تم استلام طلبك من قبل الإدارة. تواصل معهم للحصول على الكود المكوّن من <b>6 أرقام</b>، ثم أدخله أدناه مع كلمة المرور الجديدة.
                </span>
              </div>
              <label className="text-sm font-bold">الكود من الإدارة (6 أرقام)</label>
              <input
                type="text" inputMode="numeric" maxLength={6} value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="mt-1 w-full px-4 py-3 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none text-center tracking-[8px] font-black text-2xl"
                placeholder="——————"
                autoComplete="one-time-code"
                data-testid="forgot-code"
              />
              <label className="text-sm font-bold mt-4 block">كلمة المرور الجديدة</label>
              <PasswordInput
                value={password} onChange={(e) => setPassword(e.target.value)}
                inputClassName="mt-1 w-full px-4 py-3 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none"
                placeholder="6 أحرف على الأقل"
                autoComplete="new-password"
                testId="forgot-newpass"
              />
              <label className="text-sm font-bold mt-4 block">تأكيد كلمة المرور</label>
              <PasswordInput
                value={password2} onChange={(e) => setPassword2(e.target.value)}
                inputClassName="mt-1 w-full px-4 py-3 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none"
                placeholder="••••••"
                autoComplete="new-password"
                testId="forgot-newpass2"
              />
              {err && <div className="mt-3 text-sm bg-red-50 text-red-600 px-3 py-2 rounded-lg" data-testid="forgot-error">{err}</div>}
              <button disabled={busy} onClick={reset}
                className="mt-5 w-full py-3 rounded-xl bg-primary-brand text-white font-black disabled:opacity-60"
                data-testid="forgot-submit">
                {busy ? "جاري التعيين…" : "إعادة تعيين كلمة المرور"}
              </button>

              <div className="mt-4">
                <SupportContactsCard variant="compact" title="لم تستلم الكود بعد؟" subtitle="تواصل مع الإدارة"/>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
